import { parseCommaSeparatedValues } from '@/app/utils/stringUtils';
import { buildCollectionWithCache } from '@/app/utils/query-builder';

const applyFieldFilter = (query, fieldName, filterValue) => {
    if (!filterValue || filterValue.length === 0) return query;

    const items = parseCommaSeparatedValues(filterValue);
    items.forEach((item) => {
        query = query.field(fieldName).equals(item);
    });

    return query;
};

export const applyFiltersToQuery = (query, relatedBlogsCategories, relatedBlogTags) => {
    query = applyFieldFilter(query, 'categories', relatedBlogsCategories);
    query = applyFieldFilter(query, 'tags', relatedBlogTags);
    return query;
};

function getAuthorsQuery(authors, contentType) {
    if (
        !authors ||
        (Array.isArray(authors) && authors.length === 0) ||
        (typeof authors === 'string' && authors.trim() === '')
    ) {
        return '';
    }

    const authorValues = parseCommaSeparatedValues(authors);

    if (authorValues.length === 0) return '';

    const qualifiedField = `${contentType}.author`;
    const orQuery = authorValues.map((val) => `${qualifiedField}:"${val}"`).join(' OR ');
    return `+(${orQuery})`;
}

async function buildContentQuery({
    contentType,
    currentTitle,
    listingField,
    showInListing,
    categories,
    tags,
    extractedCategories,
    authors,
    sortBy,
    order,
    limit,
    // Field names on the target content type. Default to `categories`/`tags`
    // for Blog-shaped types; override per content type when its schema differs
    // (e.g. CaseStudy uses `industry`).
    categoryField = 'categories',
    tagField = 'tags'
}) {
    const authorsQuery = getAuthorsQuery(authors, contentType);

    let queryString = '';
    const queryParts = [];

    if (extractedCategories && extractedCategories.length > 0 && extractedCategories[0]) {
        const categoryName =
            extractedCategories[0]?.name ||
            (typeof extractedCategories[0] === 'string' ? extractedCategories[0] : '');
        queryParts.push(`+${contentType}.${categoryField}:${categoryName}`);
    }

    if (currentTitle && currentTitle.trim() !== '') {
        queryParts.push(`-${contentType}.title:${currentTitle}`);
    }

    if (showInListing !== undefined) {
        if (showInListing === 'true' && listingField) {
            queryParts.push(`+${contentType}.${listingField}:true`);
        } else if (listingField) {
            queryParts.push(`-${contentType}.${listingField}:true`);
        }
    } else if (listingField) {
        queryParts.push(`+${contentType}.${listingField}:true`);
    }

    if (!extractedCategories || extractedCategories.length === 0 || !extractedCategories[0]) {
        // OR semantics for multi-value category/tag filters: one clause matches
        // ANY selected value. A single value collapses to the same shape it
        // had before so existing single-value callers are unaffected.
        if (categories && categories.trim() !== '') {
            const categoryItems = parseCommaSeparatedValues(categories);
            if (categoryItems.length > 0) {
                const clause = categoryItems
                    .map((item) => `${contentType}.${categoryField}:${item}`)
                    .join(' ');
                queryParts.push(`+(${clause})`);
            }
        }

        if (tags && tags.trim() !== '') {
            const tagItems = parseCommaSeparatedValues(tags);
            if (tagItems.length > 0) {
                const clause = tagItems
                    .map((item) => `${contentType}.${tagField}:${item}`)
                    .join(' ');
                queryParts.push(`+(${clause})`);
            }
        }
    }

    if (authorsQuery) {
        queryParts.push(authorsQuery);
    }

    queryString = queryParts.join(' ');

    const normalizedSortBy =
        typeof sortBy === 'string'
            ? { field: sortBy, order: order || 'desc' }
            : sortBy || { field: 'modDate', order: order || 'desc' };
    return await buildCollectionWithCache({
        contentType,
        queryConfig: {
            sortBy: normalizedSortBy
        },
        limit,
        defaultQuery: queryString,
        cacheKeyPrefix: 'relatedContent',
        depth: 1
    });
}

function calculateManualCountAndTotalLimit(showPosts, limit) {
    const actualManualCount = showPosts && Array.isArray(showPosts) ? showPosts.length : 0;
    const totalLimit = Math.max(0, limit - actualManualCount);
    return { actualManualCount, totalLimit };
}

function handleManualOnlySelection(showPosts, manualLimit) {
    if (showPosts && Array.isArray(showPosts) && showPosts.length > 0 && manualLimit > 0) {
        return showPosts.slice(0, manualLimit);
    }
    return [];
}

function handleSortingLogic(
    relatedContentletsResponse,
    showPosts,
    orderByType,
    autoLimit,
    manualLimit,
    totalLimit
) {
    const relatedContent = [];
    const isAutoFirst = orderByType === 'auto';
    const isManualFirst = orderByType === 'manual';

    const autoContentlets =
        relatedContentletsResponse.contentlets &&
        Array.isArray(relatedContentletsResponse.contentlets)
            ? relatedContentletsResponse.contentlets
            : [];
    const actualAutoCount = autoContentlets.length;

    const manualContentlets = showPosts && Array.isArray(showPosts) ? showPosts : [];
    const actualManualCount = manualContentlets.length;

    let adjustedAutoLimit = Math.min(autoLimit, actualAutoCount);
    let adjustedManualLimit = Math.min(manualLimit, actualManualCount);

    const totalUsedSpace = adjustedAutoLimit + adjustedManualLimit;
    if (totalUsedSpace > totalLimit) {
        const ratio = totalLimit / totalUsedSpace;
        adjustedAutoLimit = Math.floor(adjustedAutoLimit * ratio);
        adjustedManualLimit = Math.floor(adjustedManualLimit * ratio);

        const remaining = totalLimit - (adjustedAutoLimit + adjustedManualLimit);
        if (remaining > 0) {
            if (autoLimit >= manualLimit) {
                adjustedAutoLimit += remaining;
            } else {
                adjustedManualLimit += remaining;
            }
        }
    } else {
        const remainingSpace = totalLimit - totalUsedSpace;

        if (remainingSpace > 0) {
            const emptyManualSlots = manualLimit - adjustedManualLimit;
            const availableAutoPosts = actualAutoCount - adjustedAutoLimit;
            const canAddMoreAuto = autoLimit - adjustedAutoLimit;

            if (emptyManualSlots > 0 && availableAutoPosts > 0 && canAddMoreAuto > 0) {
                const canFillWithAuto = Math.min(
                    emptyManualSlots,
                    availableAutoPosts,
                    canAddMoreAuto,
                    remainingSpace
                );
                adjustedAutoLimit += canFillWithAuto;
            }
            const emptyAutoSlots = autoLimit - adjustedAutoLimit;
            const availableManualPosts = actualManualCount - adjustedManualLimit;
            const canAddMoreManual = manualLimit - adjustedManualLimit;

            if (emptyAutoSlots > 0 && availableManualPosts > 0 && canAddMoreManual > 0) {
                const canFillWithManual = Math.min(
                    emptyAutoSlots,
                    availableManualPosts,
                    canAddMoreManual,
                    remainingSpace
                );
                adjustedManualLimit += canFillWithManual;
            }
        }
    }

    if (isAutoFirst) {
        if (autoContentlets.length > 0 && adjustedAutoLimit > 0) {
            relatedContent.push(...autoContentlets.slice(0, adjustedAutoLimit));
        }

        if (manualContentlets.length > 0 && adjustedManualLimit > 0) {
            relatedContent.push(...manualContentlets.slice(0, adjustedManualLimit));
        }
    } else if (isManualFirst) {
        if (manualContentlets.length > 0 && adjustedManualLimit > 0) {
            relatedContent.push(...manualContentlets.slice(0, adjustedManualLimit));
        }

        if (autoContentlets.length > 0 && adjustedAutoLimit > 0) {
            relatedContent.push(...autoContentlets.slice(0, adjustedAutoLimit));
        }
    } else {
        if (manualContentlets.length > 0 && adjustedManualLimit > 0) {
            relatedContent.push(...manualContentlets.slice(0, adjustedManualLimit));
        }

        if (autoContentlets.length > 0 && adjustedAutoLimit > 0) {
            relatedContent.push(...autoContentlets.slice(0, adjustedAutoLimit));
        }
    }

    return relatedContent;
}

async function handleAutoManualMixed({
    contentType,
    currentTitle,
    showInListing,
    categories,
    tags,
    sortBy,
    order,
    limit,
    authors,
    listingField,
    showPosts,
    autoLimit,
    manualLimit,
    orderByType,
    extractedCategories,
    categoryField,
    tagField
}) {
    if (autoLimit === 0) {
        return handleManualOnlySelection(showPosts, manualLimit);
    }

    const { totalLimit } = calculateManualCountAndTotalLimit(showPosts, limit);
    const relatedContentletsResponse = await buildContentQuery({
        contentType,
        currentTitle,
        listingField,
        showInListing,
        categories,
        tags,
        extractedCategories,
        authors,
        sortBy,
        order,
        limit: totalLimit,
        categoryField,
        tagField
    });

    return handleSortingLogic(
        relatedContentletsResponse,
        showPosts,
        orderByType,
        autoLimit,
        manualLimit,
        limit
    );
}

async function handleAutoGeneration({
    contentType,
    currentTitle,
    sortBy,
    order,
    limit,
    listingField,
    authors,
    extractedCategories,
    showPosts,
    showInListing,
    categories,
    tags,
    categoryField,
    tagField
}) {
    const relatedContent = [];

    if (limit === 0) {
        return relatedContent;
    }

    if (extractedCategories && extractedCategories.length > 0 && extractedCategories[0]) {
        const { totalLimit } = calculateManualCountAndTotalLimit(showPosts, limit);
        const relatedContentletsResponse = await buildContentQuery({
            contentType,
            currentTitle,
            listingField,
            showInListing,
            categories,
            tags,
            extractedCategories,
            authors,
            sortBy,
            order,
            limit: totalLimit,
            categoryField,
            tagField
        });

        if (
            relatedContentletsResponse.contentlets &&
            Array.isArray(relatedContentletsResponse.contentlets)
        ) {
            relatedContent.push(...relatedContentletsResponse.contentlets);
        }
    }

    return relatedContent;
}

async function handleAutoGenerationFallback({
    contentType,
    currentTitle,
    sortBy,
    order,
    limit,
    listingField
}) {
    const relatedContent = [];

    if (limit === 0) {
        return relatedContent;
    }

    const relatedContentletsResponse = await buildContentQuery({
        contentType,
        currentTitle,
        listingField,
        showInListing: undefined,
        categories: undefined,
        tags: undefined,
        extractedCategories: undefined,
        authors: undefined,
        sortBy,
        order,
        limit
    });

    if (
        relatedContentletsResponse.contentlets &&
        Array.isArray(relatedContentletsResponse.contentlets)
    ) {
        relatedContent.push(...relatedContentletsResponse.contentlets);
    }

    return relatedContent;
}

export async function getRelatedContent({
    offAutoGeneration,
    showPosts,
    showOnlyManualSelected,
    manualLimit = 4,
    autoLimit = 4,
    contentType,
    currentTitle,
    showInListing,
    authors,
    categories,
    tags,
    sortBy,
    order = 'desc',
    limit = 4,
    extractedCategories,
    listingField = 'showInListing',
    liveField = 'live',
    orderByType = 'manual',
    categoryField,
    tagField
}) {
    if (limit === 0) {
        return [];
    }

    if (autoLimit === 0 || showOnlyManualSelected) {
        return handleManualOnlySelection(showPosts, manualLimit);
    }

    if (offAutoGeneration && !showOnlyManualSelected) {
        return await handleAutoManualMixed({
            contentType,
            currentTitle,
            showInListing,
            categories,
            tags,
            sortBy,
            order,
            limit,
            authors,
            listingField,
            liveField,
            showPosts,
            autoLimit,
            manualLimit,
            orderByType,
            extractedCategories,
            categoryField,
            tagField
        });
    }

    if (
        !offAutoGeneration &&
        extractedCategories &&
        extractedCategories.length > 0 &&
        extractedCategories[0]
    ) {
        return await handleAutoGeneration({
            contentType,
            currentTitle,
            sortBy,
            order,
            authors,
            limit,
            listingField,
            liveField,
            extractedCategories,
            showPosts,
            showInListing,
            categories,
            tags,
            categoryField,
            tagField
        });
    }

    if (!offAutoGeneration && (contentType === 'News' || contentType === 'Video')) {
        return await handleAutoGenerationFallback({
            contentType,
            currentTitle,
            sortBy,
            order,
            limit,
            listingField,
            liveField
        });
    }

    return [];
}

export async function getRelatedBlogs({
    relatedBlogsOffAutoGeneration,
    relatedBlogsOrderByType,
    relatedBlogsShowPosts,
    relatedBlogsShowOnlyManualSelected,
    relatedBlogsManualLimit = 4,
    relatedBlogsAutoLimit = 4,
    relatedBlogShowInListing,
    relatedBlogsCategories,
    relatedBlogTags,
    relatedBlogAuthors,
    relatedBlogsSortBy = 'Blog.postingDate',
    relatedBlogsOrder = 'desc',
    relatedBlogsLimit = 4,
    extractedCategories,
    currentTitle
}) {
    const result = await getRelatedContent({
        offAutoGeneration: relatedBlogsOffAutoGeneration,
        showPosts: relatedBlogsShowPosts,
        showOnlyManualSelected: relatedBlogsShowOnlyManualSelected,
        manualLimit: relatedBlogsManualLimit,
        autoLimit: relatedBlogsAutoLimit,
        contentType: 'Blog',
        currentTitle,
        authors: relatedBlogAuthors,
        showInListing: relatedBlogShowInListing,
        categories: relatedBlogsCategories,
        tags: relatedBlogTags,
        sortBy: relatedBlogsSortBy,
        order: relatedBlogsOrder,
        limit: relatedBlogsLimit,
        listingField: 'showInListing',
        extractedCategories,
        orderByType: relatedBlogsOrderByType
    });

    return result;
}

export async function getRelatedNews({
    relatedNewsOffAutoGeneration,
    relatedNewsOrderByType,
    relatedNewsShowPosts,
    relatedNewsShowOnlyManualSelected,
    relatedNewsManualLimit = 4,
    relatedNewsAutoLimit = 4,
    relatedNewsShowInListing,
    relatedNewsCategories,
    relatedNewsTags,
    relatedNewsSortBy = 'News.publishDate',
    relatedNewsOrder = 'desc',
    relatedNewsLimit = 4,
    extractedCategories,
    currentTitle
}) {
    return getRelatedContent({
        offAutoGeneration: relatedNewsOffAutoGeneration,
        showPosts: relatedNewsShowPosts,
        showOnlyManualSelected: relatedNewsShowOnlyManualSelected,
        manualLimit: relatedNewsManualLimit,
        autoLimit: relatedNewsAutoLimit,
        contentType: 'News',
        currentTitle,
        showInListing: relatedNewsShowInListing,
        categories: relatedNewsCategories,
        tags: relatedNewsTags,
        sortBy: relatedNewsSortBy,
        order: relatedNewsOrder,
        limit: relatedNewsLimit,
        extractedCategories,
        listingField: '',
        liveField: 'live',
        orderByType: relatedNewsOrderByType
    });
}

export async function getRelatedVideos({
    relatedVideosOffAutoGeneration,
    relatedVideosOrderByType,
    relatedVideosShowPosts,
    relatedVideosShowOnlyManualSelected,
    relatedVideosManualLimit = 4,
    relatedVideosAutoLimit = 4,
    relatedVideosShowInListing,
    relatedVideosCategories,
    relatedVideosTags,
    relatedVideosSortBy = 'Video.publishDate',
    relatedVideosOrder = 'desc',
    relatedVideosLimit = 4,
    relatedVideosAuthors,
    extractedCategories,
    maxLimit = 4,
    currentTitle
}) {
    return getRelatedContent({
        offAutoGeneration: relatedVideosOffAutoGeneration,
        showPosts: relatedVideosShowPosts,
        showOnlyManualSelected: relatedVideosShowOnlyManualSelected,
        manualLimit: relatedVideosManualLimit,
        autoLimit: relatedVideosAutoLimit,
        contentType: 'Video',
        currentTitle,
        showInListing: relatedVideosShowInListing,
        categories: relatedVideosCategories,
        tags: relatedVideosTags,
        sortBy: relatedVideosSortBy,
        order: relatedVideosOrder,
        limit: maxLimit,
        extractedCategories,
        authors: relatedVideosAuthors,
        listingField: 'show',
        liveField: 'live',
        orderByType: relatedVideosOrderByType
    });
}

export async function getRelatedPodcasts({
    relatedPodcastOffAutoGeneration,
    relatedPodcastOrderByType,
    relatedPodcastShowPosts,
    relatedPodcastShowOnlyManualSelected,
    relatedPodcastManualLimit = 4,
    relatedPodcastAutoLimit = 4,
    relatedPodcastShowInListing,
    relatedPodcastCategories,
    relatedPodcastTags,
    relatedPodcastSortBy = 'Podcast.publishDate',
    relatedPodcastOrder = 'desc',
    relatedPodcastLimit = 4,
    extractedCategories,
    currentTitle,
    relatedPodcastAuthors
}) {
    return getRelatedContent({
        offAutoGeneration: relatedPodcastOffAutoGeneration,
        showPosts: relatedPodcastShowPosts,
        showOnlyManualSelected: relatedPodcastShowOnlyManualSelected,
        manualLimit: relatedPodcastManualLimit,
        autoLimit: relatedPodcastAutoLimit,
        contentType: 'Podcast',
        currentTitle,
        showInListing: relatedPodcastShowInListing,
        categories: relatedPodcastCategories,
        tags: relatedPodcastTags,
        sortBy: relatedPodcastSortBy,
        order: relatedPodcastOrder,
        limit: relatedPodcastLimit,
        extractedCategories,
        listingField: 'show',
        authors: relatedPodcastAuthors,
        liveField: 'live',
        orderByType: relatedPodcastOrderByType
    });
}

export async function getRelatedCaseStudies({
    relatedCaseStudiesOffAutoGeneration,
    relatedCaseStudiesManualShowStudies,
    relatedCaseStudiesShowOnlyManualSelected,
    relatedCaseStudiesManualLimit = 2,
    relatedCaseStudiesAutoLimit = 4,
    relatedCaseStudiesCategories,
    relatedCaseStudiesTags,
    relatedCaseStudiesSortBy = 'modDate',
    relatedCaseStudiesOrder = 'desc',
    maxLimit = 4,
    extractedCategories,
    currentTitle
}) {
    // Unchecked dotCMS checkboxes come back as `[]` (empty array), which is
    // truthy in JS — so a naïve boolean check treats them as "on". Coerce to
    // real booleans by looking for the string "true" the CMS puts inside the
    // array when the box IS checked (matches how other consumers read these
    // fields elsewhere in the app).
    const isChecked = (v) => v === true || v === 'true' || (Array.isArray(v) && v.includes('true'));

    // ES sort: system fields (modDate, sysPublishDate, etc.) live on every
    // contentlet and MUST NOT carry a `<ContentType>.` prefix — the prefixed
    // form silently returns 0 hits. Strip it defensively in case an old CMS
    // value like "CaseStudy.modDate" is still stored on existing contentlets.
    const SYSTEM_SORT_FIELDS = ['modDate', 'sysPublishDate', 'creationDate', 'publishDate', 'title'];
    const normalizeSortField = (raw) => {
        if (typeof raw !== 'string' || !raw) return raw;
        const stripped = raw.replace(/^[A-Za-z0-9_]+\./, '');
        return SYSTEM_SORT_FIELDS.includes(stripped) ? stripped : raw;
    };

    return getRelatedContent({
        // Force auto-manual mixed path in getRelatedContent — for RelatedBlogs
        // this flag means "use widget-configured categories/tags instead of
        // inferring from the host page." RelatedCaseStudies has no host-page
        // inference concept, so it's always on. The
        // `relatedCaseStudiesOffAutoGeneration` field is kept on the type for
        // future flexibility but doesn't gate execution today.
        offAutoGeneration: true,
        showPosts: relatedCaseStudiesManualShowStudies,
        showOnlyManualSelected: isChecked(relatedCaseStudiesShowOnlyManualSelected),
        manualLimit: relatedCaseStudiesManualLimit,
        autoLimit: relatedCaseStudiesAutoLimit,
        contentType: 'CaseStudy',
        currentTitle,
        categories: relatedCaseStudiesCategories,
        tags: relatedCaseStudiesTags,
        sortBy: normalizeSortField(relatedCaseStudiesSortBy),
        order: relatedCaseStudiesOrder,
        limit: maxLimit,
        extractedCategories,
        // CaseStudy uses `industry` for its category field, not `categories`.
        categoryField: 'industry',
        // CaseStudy has no "show in listing" flag — skip that filter.
        listingField: '',
        liveField: 'live'
    });
}
